<template>
  <div class="w-full">
    <div class="variable-image-container">
      <img
        v-if="hasImage"
        :src="mediaSource"
        alt="Image Preview"
        class="image-preview"
      />
      <video
        v-if="hasVideo"
        controls
        autoplay
        name="media"
        class="image-preview"
      >
        <source :src="mediaSource" type="video/mp4" />
      </video>
      <div v-if="hasDocument" class="variable-document-container">
        <div class="document-placeholder">
          <span class="document-icon">📄</span>
          <a
            :href="mediaSource"
            target="_blank"
            rel="noopener noreferrer"
            class="document-link"
          >
            Document
          </a>
        </div>
      </div>
    </div>
    <textarea
      v-model="processedString"
      rows="4"
      readonly
      class="template-input"
      :style="textHeight"
    />
    <div v-if="variables" class="template__variables-container">
      <p class="variables-label">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
      </p>
      <div
        v-for="(variable, key) in processedParams"
        :key="key"
        class="template__variable-item"
      >
        <span class="variable-label">
          {{ key }}
        </span>
        <input
          v-if="allowedFormats.includes(key)"
          v-model="processedParams[key]"
          type="text"
          class="input-container variable-input bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-600"
          style="margin-bottom: 0"
          :disabled="allowedFormats.includes(key)"
        />
        <woot-input
          v-else
          v-model="processedParams[key]"
          @keydown="preventBraces"
          @paste="handlePaste"
          type="text"
          class="variable-input"
          :styles="{ marginBottom: 0 }"
          :disabled="allowedFormats.includes(key)"
        />
      </div>
      <p v-if="v$.$dirty && v$.$invalid" class="error">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>
    <footer>
      <woot-button variant="smooth" @click="$emit('resetTemplate')">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
      </woot-button>
      <woot-button type="button" @click="sendMessage">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL') }}
      </woot-button>
    </footer>
  </div>
</template>

<script>
const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};
//import { requiredIf } from 'vuelidate/lib/validators';
import {requiredIf } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

export default {
  props: {
    template: {
      type: Object,
      default: () => {},
    },
  },
  validations: {
    processedParams: {
      requiredIfKeysPresent: requiredIf('variables'),
      allKeysRequired,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      processedParams: {},
    };
  },
  computed: {
    mediaId() {
      return JSON.parse(this.template.meta).mediaId;
    },
    mediaUrl(){
      return JSON.parse(this.template.meta).mediaUrl;
    },
    allowedFormats() {
      return ['link_video', 'link_image', 'link_document', 'link_gif'];
    },
    textHeight() {
      return this.hasMediaHeader ? 'height: 150px' : 'height: 50px';
    },
    hasMediaHeader() {
      return this.template.templateType !== 'TEXT';
    },
    hasImage() {
      return this.template.templateType === 'IMAGE';
    },
    hasVideo() {
      return ['VIDEO', 'GIF'].includes(this.template.templateType);
    },
    hasDocument() {
      return this.template.templateType === 'DOCUMENT';
    },
    mediaSource() {
      if(this.mediaUrl === undefined  || this.mediaUrl === null){
        return `https://api.gupshup.io/wa/${this.template.app_id}/wa/media/${this.mediaId}?download=false`;
      }
      return this.mediaUrl;
    },
    variables() {
      const variables = this.templateString.match(/{{([^}]+)}}/g) ?? [];
      return variables;
    },
    originalTemplateString() {
      return this.template.components.find(
        component => component.type === 'BODY'
      ).text;
    },
    templateString() {
      return this.templateMediaVariable + this.originalTemplateString;
    },
    templateMediaVariable() {
      if (this.hasMediaHeader && this.allowedFormats.includes('link_' + this.template.templateType.toLowerCase())) {
        return '{{link_' + this.template.templateType.toLowerCase() + '}}';
      }
      return '';
    },
    processedString() {
      return this.originalTemplateString.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = this.processVariable(variable);
        return this.processedParams[variableKey] || `{{${variable}}}`;
      });
    },
  },
  mounted() {
    this.generateVariables();
  },
  methods: {
    sendMessage() {
      this.v$.$touch();
      if (this.v$.$invalid && this.variables.length > 0) return;
      const payload = {
        message: this.processedString,
        templateParams: {
          name: this.template.name,
          category: this.template.category,
          language: this.template.language,
          namespace: this.template.namespace,
          processed_params: this.processedParams,
          template_id: this.template.id,
        },
      };
      this.$emit('sendMessage', payload);
      this.$emit('resetTemplate')
    },
    processVariable(str) {
      return str.replace(/{{|}}/g, '');
    },
    generateVariables() {
      const matchedVariables = this.templateString.match(/{{([^}]+)}}/g);
      if (!matchedVariables) return;

      const variables = matchedVariables.map(i => this.processVariable(i));
      const newParams = variables.reduce((acc, variable) => {
        acc[variable] = this.allowedFormats.includes(variable)
          ? this.mediaSource
          : '';
        return acc;
      }, {});
      

      this.processedParams = { ...newParams };
    },

    preventBraces(event) {
      if (event.key === '{' || event.key === '}') {
        event.preventDefault();
      }
    },
    handlePaste(event) {
      event.preventDefault();
      
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      
      const sanitizedText = pastedText.replace(/[{}]/g, '');
      
      const target = event.target;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      
      const inputElement = event.target;
      const variableItem = inputElement.closest('.template__variable-item');
      const variableLabel = variableItem.querySelector('.variable-label');
      const key = variableLabel.textContent.trim();
      
      const currentValue = this.processedParams[key] || '';
      const newValue = currentValue.substring(0, start) + sanitizedText + currentValue.substring(end);
      
      this.processedParams[key] = newValue;
      
      this.$forceUpdate();
      
      setTimeout(() => {
        target.setSelectionRange(start + sanitizedText.length, start + sanitizedText.length);
        target.focus();
      }, 0);
    }
  },
};
</script>

<style scoped lang="scss">
.template__variables-container {
  @apply p-2.5;
}

.variables-label {
  @apply text-sm font-semibold mb-2.5;
}

.template__variable-item {
  @apply items-center flex mb-2.5;

  .label {
    @apply text-xs;
  }

  .variable-input {
    @apply flex-1 text-sm ml-2.5;
  }

  .variable-label {
    @apply bg-slate-75 dark:bg-slate-700 text-slate-700 dark:text-slate-100 inline-block rounded-md text-xs py-2.5 px-6;
  }
}

footer {
  @apply flex justify-end;

  button {
    @apply ml-2.5;
  }
}

.error {
  @apply bg-red-100 dark:bg-red-100 rounded-md text-red-800 dark:text-red-800 p-2.5 text-center;
}

.template-input {
  @apply bg-slate-25 dark:bg-slate-900 text-slate-700 dark:text-slate-100;
}

.variable-image-container {
  @apply flex flex-col;

  .image-preview {
    @apply mb-2 w-full h-auto rounded-md border border-slate-300 dark:border-slate-600;
    max-height: 200px;
    object-fit: cover;
  }
}
.variable-document-container {
  @apply mb-2;
  .document-placeholder {
    @apply mt-2 flex items-center justify-center gap-1 p-4 border border-slate-300 dark:border-slate-600 rounded-md;

    .document-icon {
      @apply text-2xl text-slate-700 dark:text-slate-100;
    }

    .document-link {
      @apply text-sm text-slate-500 underline dark:text-slate-400 cursor-pointer;
    }
  }
}
</style>
